import { DestroyRef, Injectable, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Firestore
} from '@angular/fire/firestore';
import { Subscription } from 'rxjs';
import { LoggerService } from '@core/services/infra/logger.service';
import { deepStripUndefined } from '@shared/utils/firestore-clean';
import { FirestoreApi } from '@core/services/data/firestore-api.service';
import { WorkspaceStore } from '../../../../shared/services/workspace.store';
import { BillingProfile } from '../models/workspace-billing-profile.model';
import type { IWorkspaceBillingProfilesStore } from './workspace-billing-profiles-store.interface';

/**
 * Firestore implementation of the workspace-scoped billing-profiles store.
 *
 * Owns the real-time `collectionData` subscription on
 * `workspaces/{wsId}/billing-profiles` plus all CRUD operations (add, update,
 * delete, setAsDefault) including the single-default invariant. Replaces the
 * legacy `WorkspaceBillingProfileService` retired as part of Phase C
 * (2026-04-20).
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceBillingProfilesFirestoreStore implements IWorkspaceBillingProfilesStore, OnDestroy {
  private readonly CONTEXT = 'WorkspaceBillingProfilesFirestoreStore';

  private readonly firestore = inject(Firestore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fs = inject(FirestoreApi);
  private readonly logger = inject(LoggerService);
  private readonly workspaceStore = inject(WorkspaceStore);

  private readonly _profiles = signal<readonly BillingProfile[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly profiles = this._profiles.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._profiles().length);
  readonly defaultProfile = computed<BillingProfile | null>(
    () => this._profiles().find(p => p.isDefault) ?? null
  );
  readonly hasProfiles = computed(() => this._profiles().length > 0);

  private currentWorkspaceId: string | null = null;
  private sub?: Subscription;

  constructor() {
    // Re-subscribe whenever the active workspace changes.
    effect(() => {
      const workspace = this.workspaceStore.currentWorkspace();
      if (workspace?.workspaceId && workspace.workspaceId !== this.currentWorkspaceId) {
        this.currentWorkspaceId = workspace.workspaceId;
        this.fetchBillingProfiles();
      } else if (!workspace) {
        this.clearBillingProfiles();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  async refresh(): Promise<void> {
    this.fetchBillingProfiles();
  }

  loadBillingProfiles(): void {
    this.fetchBillingProfiles();
  }

  async addProfile(
    profile: Omit<BillingProfile, 'profileId' | 'ownerUID' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    if (!this.currentWorkspaceId) {
      throw new Error('No workspace selected');
    }
    if (!this.isValidIban(profile.bankingInfo.iban)) {
      throw new Error('Invalid IBAN format');
    }

    try {
      const profileId = this.createId();
      // Scrub FIRST, stamp after: `deepStripUndefined` turns class instances
      // (`Address`, `BillingProfile`) into plain objects and drops `undefined`,
      // both of which the frontend SDK rejects — the onboarding lease import
      // hands over a `new Address(...)` (#637). The server-timestamp sentinels
      // must never go THROUGH it.
      const profileToSave = {
        ...deepStripUndefined({ ...profile, profileId, ownerUID: this.currentWorkspaceId }),
        createdAt: this.fs.serverTimestamp(),
        updatedAt: this.fs.serverTimestamp()
      };

      if (profile.isDefault) {
        await this.unsetAllDefaults();
      }
      await this.fs.setDoc(this.getDocument(profileId), profileToSave);
      // Optimistic update — the UI shouldn't wait for the live listener.
      const now = new Date();
      const cleared = profile.isDefault
        ? this._profiles().map(p => ({ ...p, isDefault: false }) as BillingProfile)
        : [...this._profiles()];
      this._profiles.set([
        new BillingProfile({ ...profile, profileId, ownerUID: this.currentWorkspaceId!, createdAt: now, updatedAt: now }),
        ...cleared
      ]);
      return profileId;
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async updateProfile(
    profileId: string,
    updatedProfile: Omit<BillingProfile, 'profileId' | 'ownerUID' | 'createdAt'>
  ): Promise<void> {
    if (!this.currentWorkspaceId) {
      throw new Error('No workspace selected');
    }
    if (!this.isValidIban(updatedProfile.bankingInfo.iban)) {
      throw new Error('Invalid IBAN format');
    }

    const existing = this._profiles().find(p => p.profileId === profileId);
    if (!existing) {
      throw new Error('Billing profile not found');
    }

    try {
      if (updatedProfile.isDefault && !existing.isDefault) {
        await this.unsetAllDefaults();
      }
      await this.fs.updateDoc(this.getDocument(profileId), {
        ...deepStripUndefined(updatedProfile),
        updatedAt: this.fs.serverTimestamp()
      } as Record<string, unknown>);
      // Optimistic update.
      const now = new Date();
      this._profiles.set(
        this._profiles().map(p => {
          if (p.profileId === profileId) {
            return new BillingProfile({ ...p, ...updatedProfile, updatedAt: now });
          }
          // If we just promoted a new default, clear isDefault on the others.
          if (updatedProfile.isDefault && p.isDefault) {
            return new BillingProfile({ ...p, isDefault: false });
          }
          return p;
        })
      );
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async deleteProfile(profileId: string): Promise<void> {
    if (!this.currentWorkspaceId) {
      throw new Error('No workspace selected');
    }
    const profile = this._profiles().find(p => p.profileId === profileId);
    if (!profile) {
      throw new Error('Billing profile not found');
    }
    try {
      await this.fs.deleteDoc(this.getDocument(profileId));
      // Optimistic update — remove from the signal immediately.
      this._profiles.set(this._profiles().filter(p => p.profileId !== profileId));
      // If we just deleted the default, promote another profile as default so
      // downstream consumers (contracts, invoices) always have one to bind to.
      if (profile.isDefault) {
        const remaining = this._profiles();
        if (remaining.length > 0) {
          await this.setAsDefault(remaining[0].profileId);
        }
      }
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async setAsDefault(profileId: string): Promise<void> {
    try {
      await this.unsetAllDefaults();
      await this.fs.updateDoc(this.getDocument(profileId), {
        isDefault: true,
        updatedAt: this.fs.serverTimestamp()
      });
      // Optimistic update.
      this._profiles.set(
        this._profiles().map(p =>
          new BillingProfile({ ...p, isDefault: p.profileId === profileId })
        )
      );
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  getProfileById(profileId: string): BillingProfile | null {
    return this._profiles().find(p => p.profileId === profileId) ?? null;
  }

  // --- internals ---

  private fetchBillingProfiles(): void {
    if (!this.currentWorkspaceId) {
      this.logger.debug('No workspace ID, skipping fetch', this.CONTEXT);
      return;
    }
    this.sub?.unsubscribe();
    this._loading.set(true);
    try {
      this.sub = this.fs.collectionData(this.getCollection(), { idField: 'profileId' })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: rows => {
            const profiles = rows.map(profileData => new BillingProfile({
              ...profileData,
              createdAt: profileData['createdAt'] || new Date(),
              updatedAt: profileData['updatedAt'] || new Date()
            }));
            this._profiles.set(profiles);
            this._loading.set(false);
          },
          error: err => {
            this._lastError.set(err instanceof Error ? err : new Error(String(err)));
            this._loading.set(false);
          }
        });
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      this._loading.set(false);
    }
  }

  private async unsetAllDefaults(): Promise<void> {
    const defaults = this._profiles().filter(p => p.isDefault);
    if (defaults.length === 0) return;
    await Promise.all(defaults.map(profile =>
      this.fs.updateDoc(this.getDocument(profile.profileId), {
        isDefault: false,
        updatedAt: this.fs.serverTimestamp()
      })
    ));
  }

  private clearBillingProfiles(): void {
    this.sub?.unsubscribe();
    this.currentWorkspaceId = null;
    this._profiles.set([]);
  }

  private getCollection() {
    if (!this.currentWorkspaceId) {
      throw new Error('No workspace selected');
    }
    return this.fs.collection(this.firestore, `workspaces/${this.currentWorkspaceId}/billing-profiles`);
  }

  private getDocument(documentId: string) {
    if (!this.currentWorkspaceId) {
      throw new Error('No workspace selected');
    }
    return this.fs.doc(this.firestore, `workspaces/${this.currentWorkspaceId}/billing-profiles/${documentId}`);
  }

  private createId(): string {
    return this.fs.doc(this.fs.collection(this.firestore, 'temp')).id;
  }

  /**
   * Basic IBAN-shape validation: 2-letter country + 2 check digits + 11-30
   * alphanumeric. Strict-enough to catch typos; full mod-97 validation is a
   * backend concern.
   */
  private isValidIban(iban: string): boolean {
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    return /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleanIban)
      && cleanIban.length >= 15
      && cleanIban.length <= 34;
  }
}
